/**
 * Shared utilities + tiny presentational primitives for the Users & Agents
 * section. Kept local to this section so other workers don't pick them up
 * accidentally — promote to top-level components/ if reused later.
 */

import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';

import { cn } from '@/lib/utils';

export const DASH = '—';

export function ShortId({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="text-muted-foreground">{DASH}</span>;
  return (
    <code className="text-xs text-muted-foreground" title={value}>
      {value.slice(0, 8)}
    </code>
  );
}

/** Browser-local timestamp formatter. Returns DASH for falsy input. */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "3m ago" / "2h ago" / "5d ago" etc., browser-local. */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return DASH;
  if (ms < 0) return 'just now';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/** Format a duration (in ms) as "Hh Mm" / "Mm Ss" / "Ss". */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null || Number.isNaN(ms) || ms < 0) return DASH;
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

interface DataTableShellProps {
  loading?: boolean;
  error?: unknown;
  empty?: boolean;
  emptyLabel?: string;
  children: ReactNode;
}

/**
 * Common chrome around a list view: handles loading + error + empty in one
 * place so each tab can focus on filters + table markup.
 */
export function DataTableShell({
  loading,
  error,
  empty,
  emptyLabel = 'No results',
  children,
}: DataTableShellProps) {
  if (error) {
    const message =
      error instanceof Error ? error.message : typeof error === 'string' ? error : 'Request failed';
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-6 text-sm text-destructive">
        {message}
      </div>
    );
  }
  if (loading) {
    return (
      <div className="rounded-md border bg-card px-4 py-12 text-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (empty) {
    return (
      <div className="rounded-md border border-dashed bg-muted/20 px-4 py-12 text-center text-sm text-muted-foreground">
        {emptyLabel}
      </div>
    );
  }
  return <div className="overflow-hidden rounded-md border bg-card">{children}</div>;
}

interface DataTableProps<T> {
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  sort?: { key: string; direction: 'asc' | 'desc' };
  onSort?: (key: string) => void;
  columns: Array<{
    key: string;
    label: string;
    render: (row: T) => ReactNode;
    className?: string;
    align?: 'left' | 'right' | 'center';
    sortable?: boolean;
    sortKey?: string;
  }>;
}

export function DataTable<T>({
  rows,
  rowKey,
  onRowClick,
  sort,
  onSort,
  columns,
}: DataTableProps<T>) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr>
            {columns.map((c) => {
              const sortKey = c.sortKey ?? c.key;
              const active = sort?.key === sortKey;
              const SortIcon = active
                ? sort.direction === 'asc'
                  ? ArrowUp
                  : ArrowDown
                : ArrowUpDown;
              return (
                <th
                  key={c.key}
                  className={cn(
                    'px-4 py-2 text-left font-medium text-muted-foreground',
                    c.align === 'right' && 'text-right',
                    c.align === 'center' && 'text-center',
                    c.className,
                  )}
                >
                  {c.sortable && onSort ? (
                    <button
                      type="button"
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-sm text-left transition-colors hover:text-foreground',
                        c.align === 'right' && 'justify-end',
                      )}
                      onClick={() => onSort(sortKey)}
                      title={`Sort by ${c.label}`}
                    >
                      <span>{c.label}</span>
                      <SortIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  ) : (
                    c.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className={cn(
                'border-t transition-colors hover:bg-muted/30',
                onRowClick && 'cursor-pointer',
              )}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={cn(
                    'px-4 py-2 align-top',
                    c.align === 'right' && 'text-right',
                    c.align === 'center' && 'text-center',
                    c.className,
                  )}
                >
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface PaginationProps {
  total: number;
  limit: number;
  offset: number;
  onChange: (offset: number) => void;
}

export function Pagination({ total, limit, offset, onChange }: PaginationProps) {
  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const canPrev = offset > 0;
  const canNext = offset + limit < total;

  return (
    <div className="flex items-center justify-between border-t px-4 py-2 text-xs text-muted-foreground">
      <span>
        {total === 0 ? '0 of 0' : `${offset + 1}–${Math.min(offset + limit, total)} of ${total}`}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="rounded-md border px-2 py-1 disabled:opacity-50"
          disabled={!canPrev}
          onClick={() => onChange(Math.max(0, offset - limit))}
        >
          Prev
        </button>
        <span className="px-2">
          Page {page} / {totalPages}
        </span>
        <button
          type="button"
          className="rounded-md border px-2 py-1 disabled:opacity-50"
          disabled={!canNext}
          onClick={() => onChange(offset + limit)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

interface FilterSelectProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}

export function FilterSelect({ label, value, onChange, options }: FilterSelectProps) {
  return (
    <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
      <span>{label}</span>
      <select
        className="h-9 rounded-md border bg-background px-2 text-sm text-foreground"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

interface DateRangeProps {
  from: string;
  to: string;
  onChange: (next: { from: string; to: string }) => void;
}

export function DateRange({ from, to, onChange }: DateRangeProps) {
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <label className="flex items-center gap-1">
        <span>From</span>
        <input
          type="date"
          className="h-9 rounded-md border bg-background px-2 text-sm text-foreground"
          value={from}
          onChange={(e) => onChange({ from: e.target.value, to })}
        />
      </label>
      <label className="flex items-center gap-1">
        <span>To</span>
        <input
          type="date"
          className="h-9 rounded-md border bg-background px-2 text-sm text-foreground"
          value={to}
          onChange={(e) => onChange({ from, to: e.target.value })}
        />
      </label>
    </div>
  );
}

/** Convert a `YYYY-MM-DD` date input into an ISO datetime at boundary. */
export function toIsoStart(d: string): string | undefined {
  if (!d) return undefined;
  const dt = new Date(`${d}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? undefined : dt.toISOString();
}

export function toIsoEnd(d: string): string | undefined {
  if (!d) return undefined;
  const dt = new Date(`${d}T23:59:59`);
  return Number.isNaN(dt.getTime()) ? undefined : dt.toISOString();
}

// ── Funnel primitives ───────────────────────────────────────────────────

const FUNNEL_PILL_STAGES = [
  { rank: 1, short: 'Land' },
  { rank: 2, short: 'CTA' },
  { rank: 3, short: 'Hdl' },
  { rank: 4, short: 'Ag' },
  { rank: 5, short: 'Onb' },
  { rank: 6, short: 'Ctry' },
  { rank: 7, short: 'Pred' },
  { rank: 8, short: 'Grp' },
  { rank: 9, short: 'Arena' },
  { rank: 10, short: 'Acct' },
  { rank: 11, short: 'Ver' },
] as const;

/**
 * Eleven-segment acquisition stepper. Segments up to `furthestRank` are filled;
 * the prediction segment (rank 7) shows the prediction count when non-zero.
 */
export function FunnelStagePill({
  furthestRank,
  predictionCount = 0,
}: {
  furthestRank: number;
  predictionCount?: number;
}) {
  return (
    <div className="flex items-center gap-0.5" title={`Furthest funnel stage: ${furthestRank}/11`}>
      {FUNNEL_PILL_STAGES.map((s) => {
        const reached = furthestRank >= s.rank;
        return (
          <span
            key={s.rank}
            data-reached={reached ? 'true' : 'false'}
            className={cn(
              'rounded px-1.5 py-0.5 text-[10px] font-medium',
              reached ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
            )}
          >
            {s.rank === 7 && predictionCount > 0 ? `${s.short}·${predictionCount}` : s.short}
          </span>
        );
      })}
    </div>
  );
}

/** A titled drawer section. */
export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

/** A bordered card grouping `InfoField` rows inside a drawer section. */
export function DetailCard({ children }: { children: ReactNode }) {
  return <div className="space-y-2 rounded-md border bg-card p-3">{children}</div>;
}

/** A label/value row. */
export function InfoField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{children}</span>
    </div>
  );
}
